'use client'

import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect } from 'react'

import type {
  AuthEntityType,
  AuthModalView
} from '@/app/[locale]/components/Auth/AuthModal/types'
import { pushPartnerParams } from '@/lib/analytics/partnerParams'
import { markSignUpStartTrigger } from '@/lib/analytics/signUpStart'
import { captureTtclidFromUrl } from '@/lib/analytics/tiktokAttribution'
import {
  captureTrackingTokenFromUrl,
  getTrackingToken,
  trackVisit
} from '@/lib/analytics/trackingToken'
import { isAthleteEnabled } from '@/lib/auth/athleteFeature'
import { useModalStore } from '@/stores/modalStore'
import { isAuthenticated } from '@/utils/auth'

const REFERRAL_STORAGE_KEY = 'arena_referral_code'

// Athlete only while the athlete flag is on (see athleteFeature).
const isAuthEntityType = (value: string): value is AuthEntityType =>
  value === 'USER' ||
  value === 'CLUB' ||
  (value === 'ATHLETE' && isAthleteEnabled())

const DIRECT_AUTH_DEEP_LINKS: Record<
  string,
  { view: AuthModalView; entityType?: AuthEntityType }
> = {
  login: { view: 'login' },
  signup: { view: 'emailSignupEntityChoose' },
  signupUser: { view: 'emailSignupEntityChoose', entityType: 'USER' },
  signupClub: { view: 'emailSignupEntityChoose', entityType: 'CLUB' },
  signupAthlete: { view: 'emailSignupEntityChoose', entityType: 'ATHLETE' }
}

export function AuthModalHashHandler() {
  const searchParams = useSearchParams()
  const pathname = usePathname()

  // OCE-566 (BUG-B): capture ?ref=<code> from URL for fan referral attribution
  // across Email SignUp and Google OAuth flows (OCE-555 backend support).
  // Persisted in sessionStorage so the Google OAuth redirect round-trip keeps
  // the code; modalStore is the in-memory source of truth the hook reads.
  useEffect(() => {
    const refParam = searchParams.get('ref')
    if (refParam) {
      const trimmed = refParam.trim()
      if (trimmed) {
        const store = useModalStore.getState()
        store.setReferralCode(trimmed)
        store.setReferralEntityType('USER')
        pushPartnerParams(trimmed)
        try {
          sessionStorage.setItem(REFERRAL_STORAGE_KEY, trimmed)
        } catch {
          // sessionStorage may be unavailable (private mode); silent best-effort.
        }

        // OCE-984: фан, перешедший по /r/<code>, должен сразу видеть форму
        // регистрации с предзаполненным кодом — без этого лендинг сбивает
        // ожидания. Залогиненному модал не нужен: код уже сохранён в store
        // на случай ре-логина.
        if (!isAuthenticated()) {
          markSignUpStartTrigger('referral_link')
          store.setAuthModalInitialView('emailSignupEntityChoose')
          store.openModal('auth')
        }

        const sp = new URLSearchParams(searchParams.toString())
        sp.delete('ref')
        const qs = sp.toString()
        const hash = window.location.hash || ''
        const cleaned = `${pathname}${qs ? '?' + qs : ''}${hash}`
        window.history.replaceState(null, '', cleaned)
        return
      }
    }

    if (useModalStore.getState().referralCode === null) {
      try {
        const stored = sessionStorage.getItem(REFERRAL_STORAGE_KEY)
        if (stored) {
          const store = useModalStore.getState()
          store.setReferralCode(stored)
          store.setReferralEntityType('USER')
        }
      } catch {
        // sessionStorage may be unavailable; silent best-effort.
      }
    }
  }, [searchParams, pathname])

  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString())
    captureTtclidFromUrl(params)

    const foundParam = captureTrackingTokenFromUrl(params)
    if (!foundParam) return

    // Событие 0 (визит): свежий заход по рекламе — форвардим на бэк fire-and-forget.
    trackVisit(getTrackingToken() ?? '')

    const sp = new URLSearchParams(searchParams.toString())
    sp.delete(foundParam)
    const qs = sp.toString()
    const hash = window.location.hash || ''
    const cleaned = `${pathname}${qs ? '?' + qs : ''}${hash}`
    window.history.replaceState(null, '', cleaned)
  }, [searchParams, pathname])

  useEffect(() => {
    const view = searchParams.get('authModal')
    if (!view) return

    // US-ATH-02.3: a Google sign-up came back without an account — cancelled,
    // or refused (AU-206 email taken, AU-229 email not verified). Re-open the
    // sign-up form with the role and the consent tick it left with.
    if (view === 'signupReturn') {
      const role = searchParams.get('role')
      const authError = searchParams.get('authError')
      if (!isAuthenticated() && role && isAuthEntityType(role)) {
        const store = useModalStore.getState()
        store.setSignupDefaultEntityType(role)
        store.setSignupPrefill({
          consent: true,
          ...(authError ? { noticeCode: authError } : {})
        })
        store.setAuthModalInitialView('emailSignup')
        store.openModal('auth')
      }
      const sp = new URLSearchParams(searchParams.toString())
      sp.delete('authModal')
      sp.delete('role')
      sp.delete('authError')
      const qs = sp.toString()
      window.history.replaceState(
        null,
        '',
        `${pathname}${qs ? '?' + qs : ''}${window.location.hash || ''}`
      )
      return
    }

    const link = DIRECT_AUTH_DEEP_LINKS[view]
    // With the athlete flag off #signupAthlete opens the plain role screen.
    const deepLink =
      link?.entityType === 'ATHLETE' && !isAthleteEnabled()
        ? { view: link.view }
        : link
    const isGoogleOrSuccess = view === 'googleRoleChoose' || view === 'success'
    if (!deepLink && !isGoogleOrSuccess) return

    const store = useModalStore.getState()
    let dropHash = false

    if (view === 'googleRoleChoose') {
      const hash = window.location.hash.startsWith('#')
        ? window.location.hash.slice(1)
        : window.location.hash
      const hashParams = new URLSearchParams(hash)
      const gst = hashParams.get('gst')
      if (!gst) return

      store.setGoogleSignupToken(gst)
      dropHash = true
    }

    if (deepLink) {
      if (!isAuthenticated()) {
        store.setAuthModalInitialView(deepLink.view)
        if (deepLink.entityType) {
          store.setSignupDefaultEntityType(deepLink.entityType)
        }
        store.openModal('auth')
      }
    } else {
      store.setAuthModalInitialView(view as AuthModalView)
      store.openModal('auth')
    }

    const sp = new URLSearchParams(searchParams.toString())
    sp.delete('authModal')
    const qs = sp.toString()
    const hash = dropHash ? '' : window.location.hash || ''
    const cleaned = `${pathname}${qs ? '?' + qs : ''}${hash}`
    window.history.replaceState(null, '', cleaned)
  }, [searchParams, pathname])

  return null
}
